import { useCallback, useContext, useEffect, useMemo } from "react";

import { usePromise } from "@touchtech/web-hooks";

import { PreferenceKey } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useConfiguration } from "@is/hooks";
import { api, createPreferencesUrl } from "@is/services";
import { PreferenceFilter, PreferenceOption } from "@is/types";
import {
  convertCustomAttributesToPreferences,
  sortPreferencesByAttributeOrder,
} from "@is/utils/attributes";

export type UpdatePreferencesPayload = {
  filters: PreferenceFilter[];
  appliedPreferenceOptions: PreferenceOption[];
};

type Params = UpdatePreferencesPayload;

type UpdatePreferencesState = {
  isPending: boolean;
  error: Error | null;
  data: PreferenceOption[] | null;
};

async function handlePreferencesUpdate(
  params: Params
): Promise<PreferenceOption[]> {
  const url = createPreferencesUrl();

  const data = (await api.post(url, params)) as PreferenceOption[];

  return data;
}

export const useUpdatePreferences = (): [
  UpdatePreferencesState,
  (params: Params) => Promise<PreferenceOption[]>
] => {
  const [{ isPending, error, data }, dispatch] =
    usePromise<PreferenceOption[]>();

  const { updateViewState, tabs } = useContext(ViewStateContext).content;

  const updatePreferences = useCallback(
    (params: Params) => dispatch(handlePreferencesUpdate(params)),
    [dispatch]
  );

  const { attributes, customAttributes, isLoading } = useConfiguration({
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
  });

  const updatedPreferences: PreferenceOption[] = useMemo(() => {
    const initialPreferences = data as PreferenceOption[];

    if (isLoading || !attributes || !initialPreferences) {
      return [];
    }

    let preferences = initialPreferences;

    if (customAttributes.length) {
      const customAttributesList =
        convertCustomAttributesToPreferences(customAttributes);

      preferences = [...initialPreferences, ...customAttributesList];
    }

    const orderedPreferences = sortPreferencesByAttributeOrder(
      preferences,
      attributes
    );

    const newPreferences = orderedPreferences?.map(
      (preference: PreferenceOption, index: number) => ({
        ...preference,
        options: preference.options.map((item: PreferenceOption) => ({
          ...item,
          parentIndex:
            preference.key === PreferenceKey.Category ? index : undefined,
          parentKey: preference.key,
          options: item.options
            ? item.options.map((subItem: PreferenceOption) => ({
                ...subItem,
                parentKey: PreferenceKey.Subcategory,
                categoryKey: item.key,
              }))
            : [],
        })),
      })
    );

    return newPreferences;
  }, [data, attributes, customAttributes, isLoading]);

  useEffect(() => {
    if (updatedPreferences?.length && attributes && !tabs?.length) {
      const preferencesForTabs = updatedPreferences.reduce(
        (acc: PreferenceOption[], current) => {
          if (
            attributes.some(
              (attribute) => attribute.key === current.key && attribute.visible
            )
          ) {
            acc.push(current);
          }
          return acc;
        },
        []
      );

      // An empty tab list is not worth writing: `updateViewState` always
      // produces a new state object, so writing `[]` over the already empty
      // `tabs` changes its identity and re-triggers this effect on every
      // render — an update loop that also keeps the drawer's request effect
      // firing. Nothing to show means nothing to write.
      if (preferencesForTabs.length) {
        updateViewState({
          tabs: preferencesForTabs,
        });
      }
    }
  }, [tabs, attributes, updatedPreferences]);

  return [{ isPending, error, data: updatedPreferences }, updatePreferences];
};
